import { useMemo } from "react";
import { PaginationProps } from "../pagination";

type UsePaginationArgs = {
  totalCount: number;
  currentPage: number;
  size: PaginationProps["size"];
};

export const ellipse = "...";

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, index) => index + start);
};

const firstPageIndex = 1;

export const usePagination = ({
  totalCount,
  currentPage,
  size,
}: UsePaginationArgs) => {
  const paginationRange = useMemo(() => {
    const startEndPageCount = size === "default" ? 5 : 3;
    const minimumPagesWithoutConcatenation = startEndPageCount + 2;
    if (totalCount <= minimumPagesWithoutConcatenation) {
      return range(firstPageIndex, totalCount);
    }

    const siblingCountWhenEllipsed = size === "default" ? 2 : 1;
    const leftSiblingIndex = currentPage - siblingCountWhenEllipsed;
    const rightSiblingIndex = currentPage + siblingCountWhenEllipsed;
    const shouldShowLeftEllipse = currentPage > startEndPageCount - 1;
    const shouldShowRightEllipse =
      currentPage <= totalCount - (startEndPageCount - 1);
    const arrayWithRightEllipse =
      !shouldShowLeftEllipse && shouldShowRightEllipse;
    const arrayWithLeftEllipse =
      shouldShowLeftEllipse && !shouldShowRightEllipse;
    const arrayWithLeftAndRightEllipse =
      shouldShowLeftEllipse && shouldShowRightEllipse;

    if (arrayWithRightEllipse) {
      const leftRange = range(1, startEndPageCount);
      return [...leftRange, ellipse, totalCount];
    } else if (arrayWithLeftEllipse) {
      const rightItemCount = totalCount - (startEndPageCount - 1);
      const rightRange = range(rightItemCount, totalCount);
      return [firstPageIndex, ellipse, ...rightRange];
    } else if (arrayWithLeftAndRightEllipse) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, ellipse, ...middleRange, ellipse, totalCount];
    } else {
      return range(firstPageIndex, totalCount);
    }
  }, [totalCount, currentPage, size]);

  return paginationRange;
};
